import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  UserCheck, Search, Phone, Mail, FileText, Download, ChevronDown, ChevronUp, User, Eye,
  Award, Camera
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';

const ResponsableTuteurs = ({ user }) => {
  const [tuteurs, setTuteurs] = useState([]);
  const [centreNom, setCentreNom] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTuteur, setExpandedTuteur] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tutRes, centreRes] = await Promise.all([
        axios.get('/responsable/tuteurs'),
        axios.get('/responsable/mon-centre')
      ]);
      
      // Afficher immédiatement sans enrichissement lourd
      setTuteurs((tutRes.data || []).map(t => ({ ...t, documents: [] })));
      setCentreNom(centreRes.data?.nom || '');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Charger profil + documents uniquement quand on déplie un tuteur
  const loadTuteurDetails = async (userId) => {
    try {
      const [profileRes, docsRes] = await Promise.all([
        axios.get(`/tuteurs/${userId}`).catch(() => ({ data: {} })),
        axios.get(`/documents/${userId}`).catch(() => ({ data: [] }))
      ]);
      setTuteurs(prev => prev.map(t => {
        if (t.user_id === userId) {
          return { ...t, ...(profileRes.data?.profile || {}), documents: docsRes.data || [], _loaded: true };
        }
        return t;
      }));
    } catch (e) {
      // Silently fail
    }
  };

  // Voir un document
  const viewDocument = async (documentId) => {
    try {
      toast.info('Chargement du document...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      } else {
        toast.error('Document non disponible');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  // Télécharger un document
  const downloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredTuteurs = tuteurs.filter(t => {
    const nom = t.user_info?.nom || t.nom || '';
    const prenom = t.user_info?.prenom || t.prenom || '';
    const email = t.user_info?.email || '';
    return `${nom} ${prenom} ${email}`.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="responsable-tuteurs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Consultation seule</span>
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A]">
            Tuteurs - Centre {centreNom}
          </h1>
          <p className="text-slate-600">{tuteurs.length} tuteur(s) rattaché(s) aux stagiaires du centre</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Rechercher un tuteur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-4">
        <UserCheck className="h-8 w-8 text-purple-600" />
        <div>
          <p className="text-2xl font-bold text-purple-700">{tuteurs.length}</p>
          <p className="text-sm text-purple-600">Tuteurs dans ce centre</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredTuteurs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <UserCheck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun tuteur rattaché à ce centre</p>
          </div>
        ) : (
          filteredTuteurs.map(tuteur => {
            const isExpanded = expandedTuteur === tuteur.user_id;
            const nom = tuteur.user_info?.nom || tuteur.nom || '';
            const prenom = tuteur.user_info?.prenom || tuteur.prenom || '';
            const fullName = `${prenom} ${nom}`.trim() || 'Tuteur';
            const diplomes = tuteur.documents?.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            const autresDocs = tuteur.documents?.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')) || [];
            
            return (
              <div
                key={tuteur.user_id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
              >
                {/* En-tête */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    const newId = isExpanded ? null : tuteur.user_id;
                    setExpandedTuteur(newId);
                    if (newId && !tuteur._loaded) loadTuteurDetails(newId);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-18 rounded-md overflow-hidden bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-xl font-bold">
                        {(tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite) ? (
                          <img src={tuteur.photo || tuteur.photo_identite || tuteur.user_info?.photo_identite} alt={fullName} className="w-full h-full object-cover" />
                        ) : (
                          fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A]">
                          {tuteur.genre || ''} {fullName}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {(tuteur.user_info?.email || tuteur.email) && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" /> {tuteur.user_info?.email || tuteur.email}
                            </span>
                          )}
                          {tuteur.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {tuteur.telephone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        {diplomes.length} diplôme(s)
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full">
                        {autresDocs.length} doc(s)
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-purple-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Colonne 1: Profil complet */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <User className="h-5 w-5" />
                          Profil Complet
                        </h4>
                        
                        <div className="flex justify-center mb-4">
                          <div className="w-20 h-28 rounded-md overflow-hidden bg-purple-100 border-4 border-purple-200">
                            {tuteur.photo ? (
                              <img src={tuteur.photo} alt={fullName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-purple-400">
                                <Camera className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Genre</span>
                            <span className="font-medium">{tuteur.genre || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{nom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Prénom</span>
                            <span className="font-medium">{prenom || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Âge</span>
                            <span className="font-medium">{tuteur.age || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{tuteur.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-xs">{tuteur.user_info?.email || tuteur.email || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Diplôme principal</span>
                            <span className="font-medium">{tuteur.diplome || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Année diplôme</span>
                            <span className="font-medium">{tuteur.annee_diplome || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Lien professionnel</span>
                            <span className="font-medium capitalize">{tuteur.lien_professionnel || '-'}</span>
                          </div>
                        </div>

                        {tuteur.signature && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <p className="text-sm text-slate-500 mb-2">Signature</p>
                            <img src={tuteur.signature} alt="Signature" className="max-h-16 border border-slate-200 rounded" />
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Diplômes */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <Award className="h-5 w-5" />
                          Diplômes ({diplomes.length})
                        </h4>
                        {diplomes.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <Award className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun diplôme uploadé</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {diplomes.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100">
                                <div className="flex items-center gap-2">
                                  <Award className="h-4 w-4 text-purple-600" />
                                  <span className="text-sm font-medium text-purple-800 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Diplôme'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'diplome'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Colonne 3: Autres documents */}
                      <div className="bg-white p-5 rounded-xl border border-purple-200 shadow-sm">
                        <h4 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Autres Documents ({autresDocs.length})
                        </h4>
                        {autresDocs.length === 0 ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun autre document</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {autresDocs.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-slate-600" />
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">
                                    {doc.document_type || doc.type || 'Document'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_${fullName}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ResponsableTuteurs;
